import { GridColDef, GridRenderCellParams } from "esseal-data-table";
import { Chip, IconButton } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { GetGrantRequestsQuery, PermissionActionEnum, PermissionEntityEnum } from "@/graphql/generated/graphql";
import PermissionContext from "@/Contexts/Permissions";
import { useContext } from "react";
import RequestPermissionTooltip from "@/components/RequestPermissionTooltip";
import DateTimeFormatter from "@/shared-components/DateTimeFormatter";

export type GrantRequestRow = GetGrantRequestsQuery["getGrantRequests"][number];

const STATUS_COLOR: Record<string, "warning" | "success" | "error" | "default"> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const grantRequestsColumns = (
  handleApproveClick: (row: GrantRequestRow) => void,
  handleReject: (id: string) => void,
): GridColDef<GrantRequestRow>[] => {
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { PermissionManagement} = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, PermissionManagement);

  const sharedPermissionTooltipProps = {
    hasPermission,
    requiredPermission: { action: Manage, entity: PermissionManagement },
    checkRequestExists,
    onRequest: requestPermission,
  }

  return [
    {
      field: "requestorName",
      headerName: "Requestor",
      width: 220,
      renderCell: ({ row }: GridRenderCellParams<GrantRequestRow>) =>
        row.requestorEmployment?.fullName ?? "—",
    },
    {
      field: "requestFor",
      headerName: "Permission Requested",
      width: 320,
      renderCell: ({ row }: GridRenderCellParams<GrantRequestRow>) => row.requestFor,
    },
    {
      field: "project",
      headerName: "Project",
      width: 200,
      renderCell: ({ row }: GridRenderCellParams<GrantRequestRow>) =>
        row.project?.title ?? "All projects",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: ({ row }: GridRenderCellParams<GrantRequestRow>) => (
        <Chip size="small" label={row.status} color={STATUS_COLOR[row.status] ?? "default"} variant="outlined" />
      ),
    },
    {
      field: "actorName",
      headerName: "Resolved By",
      width: 200,
      renderCell: ({ row }: GridRenderCellParams<GrantRequestRow>) =>
        row.actorEmployment?.fullName ?? "—",
    },
    {
      field: "notes",
      headerName: "Notes",
      width: 240,
      renderCell: ({ row }: GridRenderCellParams<GrantRequestRow>) => row.notes ?? "—",
    },
    {
      field: "createdAt",
      headerName: "Requested On",
      width: 230,
      renderCell: ({ value }: GridRenderCellParams<GrantRequestRow>) => {
        return value ? <DateTimeFormatter rawDate={value} includeTime includeTimeTooltip/> : "—"
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams<GrantRequestRow>) => {
        if (row.status !== "pending") return null;

        return (
          <>
            <RequestPermissionTooltip {...sharedPermissionTooltipProps} title="Approve">
              <IconButton onClick={() => handleApproveClick(row)} disabled={!hasPermission}>
                <CheckIcon sx={{ color: hasPermission ? "success.main" : "text.disabled" }} />
              </IconButton>
            </RequestPermissionTooltip>
            <RequestPermissionTooltip {...sharedPermissionTooltipProps} title="Reject">
              <IconButton onClick={() => handleReject(row.id)} disabled={!hasPermission}>
                <CloseIcon sx={{ color: hasPermission ? "error.main" : "text.disabled" }} />
              </IconButton>
            </RequestPermissionTooltip>
          </>
        );
      },
    },
  ]
};

export default grantRequestsColumns;
